import React, {useCallback, useState} from "react";
import {Button, Card, Checkbox, Divider, Form, Input, Row, Select, theme} from "antd";
import {observer} from "mobx-react";
import {useRequiredStore} from "../../../../../utils/store";
import {AgentDataStoreContext} from "../../agent.data.store";
import {useTranslate} from "../../../../../hooks/translate.hook";
import {RootStoreContext} from "../../../../../stores/root/root.store";
import {isAllowed} from "../../../../../utils/helpers";
import {agentService} from "../../../../../services/agents/agent.service";
import {accountService} from "../../../../../services/accounts/account.service";
import {notifications} from "../../../../../services/notifications/notifications";
import {
    UiInputWidgetPurchasingAlliance,
} from "../../../../common/ui/input/widget/settings/ui.input.widget.purchasing-alliance";

export const AgentDataAgentAgent: React.FC = observer(() => {
    const {account, loadData} = useRequiredStore(AgentDataStoreContext);
    const {langStore, commonStore, authStore} = useRequiredStore(RootStoreContext);
    const t = useTranslate();
    const {token} = theme.useToken();
    const [loading, setLoading] = useState<boolean>(false);
    const [form] = Form.useForm();

    if (!account) {
        return <></>;
    }

    const submit = async (values: any) => {
        setLoading(true);
        try {
            await accountService.updateAgent(account.id, values);
            await loadData();
            notifications.successfully();
        } catch (e: any) {
            if (e.response?.data.fields) {
                form.setFields(e.response.data.fields);
            }
        } finally {
            setLoading(false);
        }
    };

    const agent = account.agent;
    const defaultValues = {
        group: agent.group,
        country: agent.country.id,
        number: agent.number,
        groupNumber: agent.groupNumber,
        name: agent.name,
        businessName: agent.businessName,
        visibleName: agent.visibleName,
        comment: agent.comment,
        icAccessGroup: agent.icAccessGroup?.id,
        purchasingAlliance: agent.purchasingAlliance?.id,
        purchasingAlliance2: agent.purchasingAlliance2?.id,
    };

    const handleNumberChange = useCallback((value: string) => {
        if (value.length >= 3 || account.agent.number !== value) {
            agentService.getByNumber(value).then((foundAgent) => {
                if (foundAgent.branches.some(({id}) => id === authStore.branch)) {
                    form.setFields([
                        {
                            name: "number",
                            errors: [t("AGENTS.DUPLICATE_ERROR")],
                        },
                    ]);
                } else {
                    form.setFieldsValue({
                        agent: {
                            ...foundAgent,
                            country: foundAgent.country?.id,
                        },
                    });
                }
            });
        }
    }, [account.agent.number, authStore.branch, form, t]);

    return (
        <Form
            size="middle"
            form={form}
            layout="vertical"
            name="agent_details_form"
            initialValues={defaultValues}
            onFinish={async (values) => {
                await form.validateFields();
                await submit(values);
            }}
        >
            <Card
                size="small"
                title={t("CLIENT.AGENT_DETAILS", {defaultValue: "Agent details"})}
                style={{width: "100%"}}
                styles={{
                    header: {
                        color: token.colorTextSecondary,
                        height: 40,
                    },
                    body: {
                        paddingTop: token.paddingLG,
                    },
                }}
            >
                    <Form.Item
                        name="businessName"
                        label={t("BUSINESS_NAME")}
                        rules={[
                            {
                                required: true,
                                message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                            },
                        ]}
                    >
                        <Input disabled={loading} type="text" placeholder={t("USE_LEGAL_NAME")} />
                    </Form.Item>
                    <Form.Item
                        name="name"
                        label={t("CLIENT.NAME")}
                        rules={[
                            {
                                required: true,
                                message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                            },
                        ]}
                    >
                        <Input disabled={loading} type="text" placeholder={t("USE_LEGAL_NAME")} />
                    </Form.Item>
                    <Form.Item
                        name="visibleName"
                        label={t("AGENT.VISIBLE_NAME")}
                        rules={[
                            {
                                required: true,
                                message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                            },
                        ]}
                    >
                        <Input disabled={loading} type="text" placeholder={t("AGENT.VISIBLE_NAME")} />
                    </Form.Item>
                    <Form.Item
                        name="group"
                        label={t("CLIENT.GROUP")}
                        rules={[
                            {
                                required: true,
                                message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                            },
                        ]}
                    >
                        <Select disabled={loading} placeholder={t("CLIENT.GROUP")}>
                            {commonStore.agentGroups.map((item) => (
                                <Select.Option key={item} value={item}>
                                    {item}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="country"
                        label={t("COUNTRY")}
                        rules={[
                            {
                                required: true,
                                message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                            },
                        ]}
                    >
                        <Select
                            placeholder={t("COUNTRY_SELECT")}
                            showSearch
                            disabled={loading || !isAllowed(authStore.attributes, "faker")}
                            optionFilterProp="children"
                        >
                            {langStore.countries.map(({id, name}) => (
                                <Select.Option key={id as string} value={id as string}>
                                    {name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="purchasingAlliance"
                        label={t("CLIENT.PURCHASING_ALLIANCE.TIER1")}
                    >
                        <UiInputWidgetPurchasingAlliance type="tier1" />
                    </Form.Item>
                    <Form.Item
                        name="purchasingAlliance2"
                        label={t("CLIENT.PURCHASING_ALLIANCE.TIER2")}
                    >
                        <UiInputWidgetPurchasingAlliance type="tier2" />
                    </Form.Item>
                    <Form.Item name="comment" label={t("COMMENT")}>
                        <Input.TextArea
                            disabled={loading}
                            autoSize={{minRows: 2, maxRows: 4}}
                        />
                    </Form.Item>
                <Divider orientation="left" plain>
                    {t("CLIENT.IC.TITLE")}
                </Divider>
                    <Form.Item name="changeNumberEnabled" valuePropName="checked">
                        <Checkbox
                            className="checkbox-small"
                            disabled={loading}
                            onChange={(event) => {
                                if (!event.target.checked) {
                                    form.setFieldValue("number", account.agent.number);
                                }
                            }}
                        >
                            {t("CLIENT.CHANGE_CODE")}
                        </Checkbox>
                    </Form.Item>
                    <Form.Item
                        noStyle
                        shouldUpdate={(previousValues, currentValues) =>
                            previousValues.changeNumberEnabled !== currentValues.changeNumberEnabled
                        }
                    >
                        {({getFieldValue}) => (
                            <Form.Item
                                name="number"
                                label={t("CLIENT.CODE")}
                                rules={[
                                    {
                                        required: true,
                                        message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                                    },
                                ]}
                            >
                                <Input
                                    disabled={!getFieldValue("changeNumberEnabled") || loading}
                                    type="text"
                                    placeholder={t("CLIENT.CODE")}
                                    onChange={(event) => handleNumberChange(event.target.value)}
                                />
                            </Form.Item>
                        )}
                    </Form.Item>
                    <Form.Item
                        name="icAccessGroup"
                        label={t("CLIENT.IC.ACCESS_GROUP")}
                        rules={[
                            {
                                required: true,
                                message: t("FROM.ERROR.PLEASE_ENTER_VALUE"),
                            },
                        ]}
                    >
                        <Select disabled={loading} placeholder={t("CLIENT.IC.ACCESS_GROUP")}>
                            {commonStore.agentICAccessGroups.map((item) => (
                                <Select.Option key={item.id} value={item.id}>
                                    {item.name}
                                </Select.Option>
                            ))}
                        </Select>
                    </Form.Item>
                    <Form.Item
                        name="groupNumber"
                        label={t("CLIENT.PARTNER_CODE")}
                    >
                        <Input disabled={loading} type="text" placeholder={t("CLIENT.PARTNER_CODE")} />
                    </Form.Item>
                <Row justify="end" style={{marginTop: 8}}>
                    <Button loading={loading} type="primary" onClick={() => form.submit()}>
                        {t("SAVE")}
                    </Button>
                </Row>
            </Card>
        </Form>
    );
});
